import { useEffect, useMemo, useRef } from 'react';
import { auth } from '../firebase/firebaseConfig';
import { useDispatch, useSelector } from 'react-redux';
import { fetchStaffs } from '../features/auth/staffSlice';

/**
 * useAdminStaffData Hook
 * 
 * For admin users, fetches the list of linked staff members and returns their UIDs.
 * For staff users, returns their own UID.
 * 
 * This hook is used to determine which user IDs to fetch data for.
 * 
 * @returns {Object} { userIds: string[], isAdmin: boolean, loading: boolean }
 */
export const useAdminStaffData = () => {
    const dispatch = useDispatch();
    const { staffs, loading: fetchStaffsLoading, lastFetched } = useSelector((state) => state.staff);
    const currentUser = auth.currentUser;

    // Detect admin status synchronously if user is available
    const isAdmin = currentUser
        ? currentUser.providerData.some(p => p.providerId === 'phone')
        : false;

    // Stabilise userIds reference – only produce a new array when the
    // actual UID list changes, not on every Redux state update.
    const prevUserIdsKeyRef = useRef('');
    const prevUserIdsRef = useRef([]);

    const userIds = useMemo(() => {
        if (!currentUser) return [];

        let result;
        if (isAdmin) {
            const staffUids = staffs.map(staff => staff.uid).sort();
            result = [currentUser.uid, ...staffUids];
        } else {
            result = [currentUser.uid];
        }

        // Only return a new array reference when the contents actually change
        const key = result.join(',');
        if (key === prevUserIdsKeyRef.current) {
            return prevUserIdsRef.current;
        }

        prevUserIdsKeyRef.current = key;
        prevUserIdsRef.current = result;
        return result;
    }, [currentUser?.uid, isAdmin, staffs]); // Use currentUser?.uid for stability

    // loading is true if we are an admin and haven't finished loading staff yet
    // or if the staff slice itself is still loading.
    // If we have already fetched (lastFetched is set), we are no longer loading even if staffs.length is 0.
    const loading = isAdmin ? (fetchStaffsLoading || (staffs.length === 0 && !lastFetched)) : false;

    useEffect(() => {
        if (!currentUser || !isAdmin) return;

        // Dispatch fetchStaffs if we don't have data AND we haven't fetched yet
        if (staffs.length === 0 && !fetchStaffsLoading && !lastFetched) {
            console.log('Fetching staff for admin:', currentUser.uid);
            dispatch(fetchStaffs(currentUser.uid));
        }
    }, [dispatch, currentUser, isAdmin, staffs.length, fetchStaffsLoading, lastFetched]);

    return { userIds, isAdmin, loading };
};

export default useAdminStaffData;
